import Highcharts from "highcharts";
import vectorInit from "highcharts/modules/vector";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

import { useSelectedTimeRange } from "@/features/dashboard/store";
import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { withVectorOpacityCull } from "@/lib/graph/with-vector-opacity-cull-options";
import { BLUE, colorGenerator, GREEN } from "@/features/shared/widgets/graphs/helpers";
import { DimensionValue } from "@/lib/dimensions";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import {
  chartSeriesIsDisabled,
  chartSeriesPresentationColor,
  formatChartSeriesName,
  getChartSeriesPresentationState,
  resolveSeriesDisplayLabel,
} from "@/components/graph/chart-series-presentation";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import { VectorPlotTimeSeriesConfig } from "./graph.types";

vectorInit(Highcharts);

/** Same relative length for every point so pixel size is uniform (see Highcharts vector docs). */
const VECTOR_RELATIVE_LENGTH = 100;

/**
 * Total Highcharts chart height (px), including title and legend.
 * Kept short for the directional “strip”; increase if multiple series rows feel cramped.
 */
const VECTOR_CHART_HEIGHT_PX = 150;

/**
 * Highcharts datetime x-axis: line series get an implicit minimum tick step via
 * `axis.closestPointRange` (ticks are not finer than data spacing). Vector extends scatter,
 * which sets `noSharedTooltip: true`, so that default is skipped and majors can appear
 * twice as often as the hourly data (see Highcharts `Axis.js` `setTickInterval`, #13369).
 *
 * HarmonIE / model-map direction series here are hourly; we pin the floor so majors align
 * with line charts. Revisit if this chart gains non-hourly data, combined axes, or if we
 * centralize tick policy in `use-graph-options.ts`.
 */
const VECTOR_X_AXIS_MIN_TICK_INTERVAL_MS = 60 * 60 * 1000;

interface VectorPlotProps {
  config: VectorPlotTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

const VectorPlot = ({ config, data, currentTime, timeBounds, height }: VectorPlotProps) => {
  const { t } = useTranslation();
  const timeRange = useSelectedTimeRange();

  const seriesCount = config.series.length;

  const seriesWithData = useMemo(() => {
    const greenColorGenerator = colorGenerator(GREEN);
    const blueColorGenerator = colorGenerator(BLUE);
    const getColor = (color: string) => {
      const fallback = "grey";
      if (color === "green") {
        return greenColorGenerator.next().value ?? fallback;
      }
      if (color === "blue") {
        return blueColorGenerator.next().value ?? fallback;
      }
      return fallback;
    };

    return config.series.map((seriesConfig, seriesIndex) => {
      const dataByParameter = data.find(
        (timeSeriesResult) => timeSeriesResult.parameter.id === seriesConfig.parameter_id,
      );
      const presentationState = getChartSeriesPresentationState(dataByParameter, timeRange);
      const displayLabel = resolveSeriesDisplayLabel(seriesConfig, dataByParameter);
      const serieName = formatChartSeriesName(displayLabel, presentationState, t);
      const rowY = seriesIndex;

      const mapped = (dataByParameter?.data?.datapoints ?? [])
        .filter(
          (d) =>
            d.value !== null && d.value !== undefined && !Number.isNaN(Number(d.value)),
        )
        .map((datapoint) => ({
          x: +new Date(datapoint.timestamp),
          y: rowY,
          length: VECTOR_RELATIVE_LENGTH,
          direction: Number(datapoint.value),
        }));

      return {
        type: "vector" as const,
        name: serieName,
        color: chartSeriesPresentationColor(
          presentationState,
          getColor(seriesConfig.color),
        ),
        enableMouseTracking: !chartSeriesIsDisabled(presentationState),
        data: mapped as Highcharts.SeriesVectorOptions["data"],
        tooltip: {
          /* Vector extends scatter, whose default header is series name; match line charts (Defaults.tooltip.headerFormat). */
          headerFormat: '<span style="font-size: 0.8em">{point.key}</span><br/>',
          pointFormatter: function (this: Highcharts.Point) {
            const direction = (this.options as { direction?: number }).direction;
            if (direction === undefined || direction === null) {
              return `<span style="color: ${this.color}">&#9679</span> ${this.series.name}: —`;
            }
            const degreesInt = Math.round(direction);
            const unitSuffix = config.unit?.symbol ?? "";
            return `<span style="color: ${this.color}">&#9679</span> ${this.series.name}: <strong>${degreesInt}${unitSuffix}</strong>`;
          },
        },
      };
    });
  }, [config, data, timeRange, t]);

  const yMax = Math.max(0, seriesCount - 1);

  const baseGraphOptions = useGraphOptions(
    {
      /*
       * Align plot area with line charts: `plotLeft` comes from `useGraphOptions`
       * `chart.marginLeft` (do not override with a smaller value). Hidden y-axis
       * (`visible: false`) does not run `axis.getOffset()`, so there is no
       * automatic label gutter—only this shared margin offsets the x-axis/grid.
       */
      chart: {
        height: resolveGraphChartHeight(height, VECTOR_CHART_HEIGHT_PX),
      },
      title: {
        text: config.title,
      },
      yAxis: [
        {
          id: "vector-y",
          min: -0.5,
          max: yMax + 0.5,
          visible: false,
          gridLineWidth: 0,
          lineWidth: 0,
          tickLength: 0,
          labels: {
            enabled: false,
          },
          title: {
            text: undefined,
          },
        },
      ],
      xAxis: [
        {
          minTickInterval: VECTOR_X_AXIS_MIN_TICK_INTERVAL_MS,
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              // Sit above the threshold fill bands (zIndex 0) but below the data series (zIndex 3).
              zIndex: 2,
            },
          ],
        },
      ],
      plotOptions: {
        vector: {
          vectorLength: 22,
        },
      },
      series: seriesWithData,
    },
    timeBounds,
  );

  const graphOptions = useMemo(
    () => withVectorOpacityCull(baseGraphOptions),
    [baseGraphOptions],
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default VectorPlot;
